import { onBeforeUnmount, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { gsap, ORG_MOTION, prefersReducedMotion } from '../utils/gsapOrg';

const isTreeChartRoute = (route) => (
  route?.name === 'tree' && route?.query?.view !== 'explore'
);

/**
 * GSAP page enter/leave for WorkspaceShell `<Transition :css="false">`.
 * Skips animation on first paint and when only route params change (same route name).
 *
 * Do not use `mode="out-in"` here: if leave is cancelled / `done()` is missed, the
 * outlet stays empty while the URL updates. Parallel leave+enter is safer with KeepAlive.
 *
 * If a GSAP tween is killed (overwrite / rapid nav / query replace mid-enter) without
 * `onComplete`, clear inline styles on the tween target and call `done()` from
 * `onInterrupt` (except Vue `*-cancelled`, which already aborted the transition).
 */
export function useShellPageTransition() {
  const skipNextTransition = ref(false);
  let isFirstPaint = true;
  let activeTween = null;
  /** @type {Element | null} */
  let activeEl = null;
  /** @type {((el?: Element) => void) | null} */
  let pendingDone = null;
  /** When true, tween kill must not invoke pendingDone (Vue cancel path). */
  let suppressDoneOnInterrupt = false;

  const clearInlineMotion = (el) => {
    if (!el) return;
    gsap.set(el, { clearProps: 'opacity,visibility,transform,willChange' });
  };

  const invokePendingDone = () => {
    const done = pendingDone;
    pendingDone = null;
    if (typeof done === 'function') done();
  };

  /** Abort tween only; used by Vue cancel hooks (do not invoke done). */
  const abortTween = (el = null) => {
    suppressDoneOnInterrupt = true;
    if (activeTween) {
      activeTween.kill();
      activeTween = null;
    }
    suppressDoneOnInterrupt = false;
    pendingDone = null;
    clearInlineMotion(el || activeEl);
    activeEl = null;
  };

  /**
   * Abort tween and complete any in-flight JS transition.
   * Always clears motion props on the animated element — route-watch unblock
   * used to call this without `el`, leaving enter stuck at opacity:0.
   */
  const completeActiveTransition = (el = null) => {
    const done = pendingDone;
    const target = el || activeEl;
    pendingDone = null;
    suppressDoneOnInterrupt = true;
    if (activeTween) {
      activeTween.kill();
      activeTween = null;
    }
    suppressDoneOnInterrupt = false;
    clearInlineMotion(target);
    activeEl = null;
    if (typeof done === 'function') done();
  };

  /**
   * @param {import('vue-router').RouteLocationNormalizedLoaded} toRoute
   * @param {{ name?: string|symbol|null|undefined, query?: Record<string, unknown> }|null} [fromRoute]
   */
  const prepareRoute = (toRoute, fromRoute = null) => {
    const toName = toRoute?.name != null ? String(toRoute.name) : null;
    const fromName = fromRoute?.name != null ? String(fromRoute.name) : null;

    skipNextTransition.value = (
      isFirstPaint
      || (fromName != null && toName != null && fromName === toName)
      || isTreeChartRoute(toRoute)
      || isTreeChartRoute(fromRoute)
    );
    isFirstPaint = false;
  };

  const finishInstant = (el, done) => {
    clearInlineMotion(el);
    activeEl = null;
    done();
  };

  const onBeforeEnter = (el) => {
    if (skipNextTransition.value || prefersReducedMotion()) return;
    // Hide before paint so enter does not flash at full opacity.
    gsap.set(el, {
      autoAlpha: 0,
      y: ORG_MOTION.pageSlideIn,
      willChange: 'transform, opacity',
    });
  };

  const onEnter = (el, done) => {
    completeActiveTransition();

    if (skipNextTransition.value || prefersReducedMotion()) {
      finishInstant(el, done);
      return;
    }

    activeEl = el;
    pendingDone = done;
    activeTween = gsap.to(el, {
      autoAlpha: 1,
      y: 0,
      duration: ORG_MOTION.pageIn,
      ease: ORG_MOTION.pageEaseIn,
      overwrite: 'auto',
      onComplete: () => {
        activeTween = null;
        activeEl = null;
        pendingDone = null;
        clearInlineMotion(el);
        done();
      },
      onInterrupt: () => {
        activeTween = null;
        if (suppressDoneOnInterrupt) {
          pendingDone = null;
          return;
        }
        clearInlineMotion(el);
        activeEl = null;
        invokePendingDone();
      },
    });
  };

  const onLeave = (el, done) => {
    completeActiveTransition();

    if (skipNextTransition.value || prefersReducedMotion()) {
      finishInstant(el, done);
      return;
    }

    activeEl = el;
    pendingDone = done;
    activeTween = gsap.to(el, {
      autoAlpha: 0,
      y: -ORG_MOTION.pageSlideOut,
      duration: ORG_MOTION.pageOut,
      ease: ORG_MOTION.pageEaseOut,
      overwrite: 'auto',
      onComplete: () => {
        activeTween = null;
        activeEl = null;
        pendingDone = null;
        clearInlineMotion(el);
        done();
      },
      onInterrupt: () => {
        activeTween = null;
        if (suppressDoneOnInterrupt) {
          pendingDone = null;
          return;
        }
        clearInlineMotion(el);
        activeEl = null;
        invokePendingDone();
      },
    });
  };

  const onEnterCancelled = (el) => {
    abortTween(el);
  };

  const onLeaveCancelled = (el) => {
    abortTween(el);
  };

  onBeforeUnmount(() => completeActiveTransition());

  return {
    prepareRoute,
    onBeforeEnter,
    onEnter,
    onLeave,
    onEnterCancelled,
    onLeaveCancelled,
    /** Call from route watcher so a stuck leave cannot block navigation forever. */
    unblockPendingTransition: () => completeActiveTransition(),
  };
}

/**
 * Wire route watchers for shell page transitions (call once in WorkspaceShell).
 * @param {(toRoute: import('vue-router').RouteLocationNormalizedLoaded, fromRoute?: object|null) => void} prepareRoute
 * @param {() => void} [onRouteChange] — unblock stuck transition when the route changes mid-tween
 */
export function bindShellPageRouteTransition(prepareRoute, onRouteChange = null) {
  const route = useRoute();
  /** @type {{ name?: string|symbol|null|undefined, query?: Record<string, unknown> }|null} */
  let previousRoute = null;

  watch(
    () => route.fullPath,
    () => {
      // Query-only replaces mid-enter used to kill the tween without clearing opacity:0.
      if (typeof onRouteChange === 'function') onRouteChange();
      prepareRoute(route, previousRoute);
      previousRoute = { name: route.name, query: { ...route.query } };
    },
    { immediate: true },
  );
}
